import { c as _c } from "react/compiler-runtime";
import { Badge, Box, Button, HStack, Icon, Image, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import { WIL_AVATAR, WIL_DISPLAY_NAME } from '@src/components/sco/Advisors/constants';
import { findLockedTeamAdvisor } from '@src/components/sco/Advisors/model/access';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { SpecialtyTag } from '@src/components/sco/Advisors/shared/SpecialtyTag';
import { askAdvisorsHref } from '@src/components/sco/Advisors/utils/mapEngagements';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { ReactNode } from 'react';
import { LuFileCheck, LuMessageSquare, LuRefreshCw, LuShield, LuSparkles } from 'react-icons/lu';

/** Static “Who is Wil.ai?” copy — mirrors handoffs/wil/who-is-wilai.html. */
const WIL_PROFILE = {
  eyebrow: WIL_DISPLAY_NAME,
  title: 'Who is Wil.ai?',
  realName: 'Wil Schroter',
  realTitle: 'Founder + CEO, Startups.com',
  quote: {
    before: "Hey Everyone, it's the ",
    highlight1: 'real Wil!',
    middle: ' I built a copy of all my experiences and thousands of hours of lessons so that I could ',
    highlight2: 'advise millions of Founders',
    after: ' 24x7, for free.'
  },
  about: {
    before: "Wil.ai is my digital twin, but it's more than just me. It's shaped by the real outcomes of the ",
    highlight: '1M+ startups',
    after: " we've worked with, so every answer is grounded in what's actually worked for Founders, not just theory."
  },
  stats: [{
    value: '9x',
    label: 'Founder'
  }, {
    value: '30+ Years',
    label: 'Startup experience'
  }, {
    value: '$1 Billion',
    label: 'Helped raise'
  }],
  whyHeading: 'Why trust my Wil.ai?',
  why: [{
    icon: <LuShield />,
    title: 'I look out for Founders.',
    body: 'All of my training is focused on helping Founders avoid obvious pitfalls and junior mistakes.'
  }, {
    icon: <LuFileCheck />,
    title: 'Based on experience.',
    body: "Unlike stock AI, I'm trained on my own experiences and working with thousands of top-tier Founders."
  }, {
    icon: <LuRefreshCw />,
    title: 'Synced 1:1 with Startups.com.',
    body: 'Plugged into my team, my tools, and my latest thinking — so it always knows where to point you.'
  }],
  personalNote: {
    before: 'Also, if you ever need the ',
    highlight: 'real me',
    after: ', just message me directly.'
  }
} as const;
const SectionLabel = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <Text color="colorPalette.fg/45" fontWeight="bold" letterSpacing="0.06em" textStyle="labelXs" textTransform="uppercase">{children}</Text>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};

/** Benefit row — Productboard / Ferndesk icon + title + body pattern. */
const WhyRow = t0 => {
  const $ = _c(12);
  const {
    icon,
    title,
    body
  } = t0;
  let t1;
  if ($[0] !== icon) {
    t1 = <Box alignItems="center" bg="purple.subtle" borderColor="purple.emphasized" borderRadius="lg" borderWidth="1px" boxSize={8} color="purple.fg" display="flex" flexShrink={0} justifyContent="center"><Icon boxSize={3.5}>{icon}</Icon></Box>;
    $[0] = icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== title) {
    t2 = <Text color="colorPalette.fg" fontFamily="heading" fontSize="sm" fontWeight="semibold" lineHeight="short">{title}</Text>;
    $[2] = title;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== body) {
    t3 = <Text color="colorPalette.fg/65" fontFamily="longform" fontSize="sm" lineHeight="tall">{body}</Text>;
    $[4] = body;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <VStack align="flex-start" flex={1} gap={0.5} minWidth={0}>{t2}{t3}</VStack>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t1 || $[10] !== t4) {
    t5 = <HStack align="flex-start" gap={3} width="full">{t1}{t4}</HStack>;
    $[9] = t1;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
};
type WilMiniProfileProps = {
  /**
   * When set, the Ask CTA invokes this in addition to navigating (e.g. close a parent modal).
   * Prefer dialog-owned footer via `hideCta` when the CTA lives outside this component.
   */
  onAsk?: () => void;
  /** Hide the top eyebrow/title when the parent dialog already provides one. */
  hideHeader?: boolean;
  /** Hide the Ask CTA (dialog footer owns the primary action). */
  hideCta?: boolean;
};

/**
 * Static “Who is Wil.ai?” content (handoffs/wil/who-is-wilai.html).
 * Rendered in a modal from the Wil.ai aside mini profile.
 *
 * Visual rhythm follows product-explainer modals (hero identity → proof stats →
 * benefit list → secondary note → optional CTA).
 */
export const WilMiniProfile = t0 => {
  const $ = _c(24);
  const {
    onAsk,
    hideHeader: t1,
    hideCta: t2
  } = t0;
  const hideHeader = t1 === undefined ? false : t1;
  const hideCta = t2 === undefined ? false : t2;
  const paths = useAdvisorsPaths();
  let t3;
  if ($[0] !== paths.base) {
    const realWil = findLockedTeamAdvisor({
      navKey: "wil-schroter"
    });
    t3 = realWil?.userId ? askAdvisorsHref(realWil.userId, paths.base) : askAdvisorsHref(null, paths.base);
    $[0] = paths.base;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const messageRealWilHref = t3;
  let t4;
  if ($[2] !== hideHeader) {
    t4 = !hideHeader ? <VStack align="flex-start" gap={1.5}><Text color="orange.fg" fontWeight="bold" letterSpacing="0.08em" textStyle="labelXs" textTransform="uppercase">{WIL_PROFILE.eyebrow}</Text><Text fontSize="lg" fontWeight="semibold" letterSpacing="-0.02em" lineHeight="short">{WIL_PROFILE.title}</Text><SpecialtyTag colorPalette="teal" label="Always on" /></VStack> : null;
    $[2] = hideHeader;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Image alt={WIL_PROFILE.realName} height="full" objectFit="cover" objectPosition="center 30%" src={WIL_AVATAR} width="full" />;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Box borderColor="colorPalette.border" borderRadius="xl" borderWidth="1px" boxShadow="sm" flexShrink={0} height="186px" overflow="hidden" position="relative" width="140px">{t5}<Box bgGradient="to-t" bottom={0} gradientFrom="black" gradientTo="transparent" gradientVia="blackAlpha.800" insetX={0} paddingBottom={3} paddingTop={10} paddingX={3} position="absolute"><Text color="white" fontSize="sm" fontWeight="bold" lineHeight="short">{WIL_PROFILE.realName}</Text><Text color="whiteAlpha.800" fontSize="2xs" lineHeight="short" marginTop={0.5}>{WIL_PROFILE.realTitle}</Text></Box></Box>;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Text aria-hidden={true} color="orange.solid" flexShrink={0} fontFamily="heading" fontSize="3xl" lineHeight={1} marginTop={-0.5} opacity={0.45}>“</Text>;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <HStack align="stretch" gap={4} width="full">{t6}<HStack align="flex-start" flex={1} gap={2} minWidth={0} paddingTop={0.5}>{t7}<Text color="colorPalette.fg" flex={1} fontFamily="heading" fontSize="md" fontStyle="italic" fontWeight="medium" letterSpacing="-0.01em" lineHeight="1.55">{WIL_PROFILE.quote.before}<Text as="span" color="orange.fg" fontStyle="italic">{WIL_PROFILE.quote.highlight1}</Text><br />{WIL_PROFILE.quote.middle.trimStart()}<Text as="span" color="orange.fg" fontStyle="italic">{WIL_PROFILE.quote.highlight2}</Text>{WIL_PROFILE.quote.after}</Text></HStack></HStack>;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Text color="colorPalette.fg/70" fontFamily="longform" fontSize="sm" lineHeight="tall">{WIL_PROFILE.about.before}<Text as="span" color="colorPalette.fg" fontFamily="heading" fontWeight="semibold">{WIL_PROFILE.about.highlight}</Text>{WIL_PROFILE.about.after}</Text>;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <SectionLabel>Who is Wil?</SectionLabel>;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <VStack align="stretch" gap={2.5}>{t10}<SimpleGrid columns={3} gap={2}>{WIL_PROFILE.stats.map(_temp)}</SimpleGrid></VStack>;
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  let t12;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <SectionLabel>{WIL_PROFILE.whyHeading}</SectionLabel>;
    $[11] = t12;
  } else {
    t12 = $[11];
  }
  let t13;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <VStack align="stretch" gap={3}>{t12}<VStack align="stretch" bg="colorPalette.bg.muted" borderColor="colorPalette.border/40" borderRadius="xl" borderWidth="1px" gap={3.5} padding={4}>{WIL_PROFILE.why.map(_temp2)}</VStack></VStack>;
    $[12] = t13;
  } else {
    t13 = $[12];
  }
  let t14;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Box alignItems="center" bg="purple.subtle" borderColor="purple.emphasized" borderRadius="lg" borderWidth="1px" boxSize={8} color="purple.fg" display="flex" flexShrink={0} justifyContent="center"><Icon boxSize={3.5}><LuMessageSquare /></Icon></Box>;
    $[13] = t14;
  } else {
    t14 = $[13];
  }
  let t15;
  if ($[14] !== messageRealWilHref) {
    t15 = <HStack align="center" borderColor="colorPalette.border/50" borderRadius="xl" borderStyle="dashed" borderWidth="1px" gap={3} paddingX={3.5} paddingY={3}>{t14}<Text color="colorPalette.fg/65" fontFamily="longform" fontSize="sm" lineHeight="tall">{WIL_PROFILE.personalNote.before}<Text asChild={true} color="orange.fg" fontFamily="heading" fontWeight="semibold" textDecoration="underline" textUnderlineOffset="2px"><NextLink href={messageRealWilHref}>{WIL_PROFILE.personalNote.highlight}</NextLink></Text>{WIL_PROFILE.personalNote.after}</Text></HStack>;
    $[14] = messageRealWilHref;
    $[15] = t15;
  } else {
    t15 = $[15];
  }
  let t16;
  if ($[16] !== hideCta || $[17] !== onAsk || $[18] !== paths.ask) {
    t16 = !hideCta ? <VStack align="stretch" gap={2} paddingTop={1}><Button asChild={true} size="md" variant="cta" width="full"><NextLink href={paths.ask} onClick={onAsk}><LuSparkles />Ask {WIL_DISPLAY_NAME}</NextLink></Button><Badge alignSelf="center" colorPalette="green" variant="subtle">Free · Always on</Badge></VStack> : null;
    $[16] = hideCta;
    $[17] = onAsk;
    $[18] = paths.ask;
    $[19] = t16;
  } else {
    t16 = $[19];
  }
  let t17;
  if ($[20] !== t15 || $[21] !== t16 || $[22] !== t4) {
    t17 = <VStack align="stretch" gap={5} width="full">{t4}{t8}{t9}{t11}{t13}{t15}{t16}</VStack>;
    $[20] = t15;
    $[21] = t16;
    $[22] = t4;
    $[23] = t17;
  } else {
    t17 = $[23];
  }
  return t17;
};
function _temp(stat) {
  return <VStack key={stat.label} bg="colorPalette.bg.muted" borderColor="colorPalette.border/50" borderRadius="xl" borderWidth="1px" gap={1.5} paddingX={1.5} paddingY={3.5} textAlign="center"><Text color="colorPalette.fg" fontSize="lg" fontWeight="bold" letterSpacing="-0.03em" lineHeight={1.1}>{stat.value}</Text><Text color="colorPalette.fg/45" fontSize="2xs" fontWeight="bold" letterSpacing="0.05em" lineHeight="short" textTransform="uppercase">{stat.label}</Text></VStack>;
}
function _temp2(row) {
  return <WhyRow key={row.title} body={row.body} icon={row.icon} title={row.title} />;
}